// exclude-from-index
import React, { PureComponent, createRef } from 'react';
import PropTypes from 'prop-types';
import {
  FormControl,
  FormHelperText,
  Input,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select as MuiSelect,
  adaptV4Theme
} from '@mui/material';
import { ThemeProvider, StyledEngineProvider, createTheme } from '@mui/material/styles';
import ConfirmationModal from '../../BaseModal/ConfirmationModal';
import Checkbox from '../Checkbox/Checkbox';
import { colors } from '../../css';
import { muiGlobalTheme } from '../FormElements.theme';
import Text from '../../BuildingBlocks/Text';

const renderOptions = ({ options, native, multiple, onConfirm, modalTitle, selectedValues, icon }) =>
  options.map(({ label, value, isExpired, isDisabled, secondaryText }) => {
    if (native) {
      return (
        <option disabled={isDisabled} key={`opt-${value}`} value={value}>
          {label}
        </option>
      );
    }

    if (multiple) {
      return (
        <MenuItem disabled={isDisabled} key={`opt-${value}`} value={value}>
          {value === '' ? (
            <ListItemText primary={label} />
          ) : (
            <>
              <Checkbox checked={selectedValues ? selectedValues.indexOf(value) > -1 : false} value={value} />
              <ListItemText primary={label} />
            </>
          )}
        </MenuItem>
      );
    }

    if (icon) {
      <MenuItem disabled={isDisabled} key={`opt-${value}`} value={value}>
        <ListItemText
          primary={label}
          secondary={secondaryText}
          onClick={event => {
            if (isExpired) {
              event.stopPropagation();
            }
          }}
        />
        {isExpired && (
          <ConfirmationModal icon={icon} handleConfirmation={onConfirm} targetValue={value} title={modalTitle} />
        )}
      </MenuItem>;
    }

    return (
      <MenuItem disabled={isDisabled} key={`opt-${value}`} value={value}>
        {value === '' ? (
          <Text.CopyBody noMargins color="tertiary">
            {label}
          </Text.CopyBody>
        ) : (
          label
        )}
      </MenuItem>
    );
  });

class Select extends PureComponent {
  static propTypes = {
    /**
     * Displays additional text (or replaces helper text) that sits below the select
     */
    errorText: PropTypes.string,
    /**
     * Displays additional text that sits below the select
     */
    helperText: PropTypes.string,
    /**
     * Provides a unique identifier used between the label and select
     */
    id: PropTypes.string.isRequired,
    /**
     * Displays text over the select and acts as a label for the select
     */
    label: PropTypes.string.isRequired,
    /**
     * Select name value
     */
    name: PropTypes.string.isRequired,
    /**
     * isDisabled: disables the use of the option and updates the styles
     * label: Option text displayed in the select dropdown
     * value: Value to return when selected
     */
    options: PropTypes.arrayOf(
      PropTypes.shape({
        isDisabled: PropTypes.bool,
        label: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired
      })
    ).isRequired,
    /**
     * Sets Select width to content width
     */
    autoWidth: PropTypes.bool,
    variant: PropTypes.oneOf(['outlined', 'standard'])
  };

  static defaultProps = {
    errorText: undefined,
    helperText: undefined,
    autoWidth: false,
    variant: 'standard'
  };

  constructor(props) {
    super(props);
    this.InputLabelRef = createRef();

    const { autoWidth } = props;

    this.muiTheme = muiGlobalTheme;

    if (!autoWidth) {
      muiGlobalTheme.overrides.MuiFormControl = {
        root: { width: '100%', minWidth: '120px', textAlign: 'left' }
      };
    }

    muiGlobalTheme.palette.text.secondary = colors.colorGrey03;
    muiGlobalTheme.overrides.MuiMenuItem = {
      root: {
        color: colors.colorBlack
      }
    };
    muiGlobalTheme.overrides.MuiInput = {
      root: {
        color: colors.colorBlack
      }
    };
    this.selectTheme = createTheme(adaptV4Theme(this.muiTheme));
  }

  render() {
    const { errorText, helperText, autoWidth, modalTitle, onConfirm, ...defaults } = this.props;
    const {
      disabled,
      id,
      error,
      native,
      multiple,
      label,
      name,
      options,
      required,
      value: selectedValues,
      variant,
      icon
    } = defaults;

    const labelWidth = this.InputLabelRef.current ? this.InputLabelRef.current.offsetWidth : 0;
    const dayInputStyles = {
      minWidth: label === 'Day' && '60px'
    };

    return (
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={this.selectTheme}>
          <FormControl disabled={disabled} error={error} variant={variant}>
            <InputLabel htmlFor={id} required={required}>
              {label}
            </InputLabel>
            <MuiSelect
              style={dayInputStyles}
              {...defaults}
              autoWidth={autoWidth}
              inputProps={{ name, id }}
              input={
                variant === 'outlined' ? (
                  <OutlinedInput id={id} name={name} labelWidth={labelWidth} />
                ) : (
                  <Input id={id} name={name} />
                )
              }
              renderValue={multiple && (() => label)}
            >
              {renderOptions({ options, native, multiple, onConfirm, modalTitle, selectedValues, icon })}
            </MuiSelect>
            {((error && errorText) || helperText) && (
              <FormHelperText>{(error && errorText) || helperText}</FormHelperText>
            )}
          </FormControl>
        </ThemeProvider>
      </StyledEngineProvider>
    );
  }
}

export default Select;
